import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { VerticalContainer, Wrapper, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import OnboardingFormContainer from 'components/containers/OnboardingFormContainer';
import OrganizationNameField from 'components/fields/OrganizationNameField';
import HeroTitleText from 'components/text/HeroTitleText';
import OnboardingFooterText from 'components/text/OnboardingFooterText';

import useOrganizations from 'hooks/useOrganizations';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { ORGANIZATION_NAME_FIELD_NAME, TEST_ID } from './constants';
import type { FunctionComponent, OnboardingOrganizationCreationFormSchema, Props } from './types';

import styles from './styles.module.scss';

const OnboardingOrganizationCreationPage: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const {
        ORGANIZATION_NAME_BODY_TEXT,
        ORGANIZATION_NAME_FOOTER_TEXT,
        ORGANIZATION_NAME_PLACEHOLDER
    } = useTranslation();
    const { createOrganization } = useOrganizations();
    const navigate = useNavigate();
    const { homePage } = usePageRoutes();

    const form = useForm<OnboardingOrganizationCreationFormSchema>({
        defaultValues: { name: '' },
        mode: 'onChange'
    });

    const {
        control,
        formState: { errors },
        handleSubmit,
        setFocus,
        trigger
    } = form;

    useEffect(() => {
        setFocus(ORGANIZATION_NAME_FIELD_NAME);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateOrganization = async (data: OnboardingOrganizationCreationFormSchema) => {
        const isValidated = await trigger();

        if (isValidated) {
            const { name } = data;

            await createOrganization({
                name
            });

            navigate(homePage);
        }
    };

    return (
        <Form form={form}>
            <Wrapper className={styles.wrapper} testId={testId}>
                <VerticalContainer className={styles.verticalContainer}>
                    <VerticalContainer>
                        <HeroTitleText text={ORGANIZATION_NAME_BODY_TEXT} />
                    </VerticalContainer>

                    <OnboardingFormContainer
                        errors={errors}
                        onNext={handleSubmit(handleCreateOrganization)}>
                        <OrganizationNameField
                            control={control}
                            isRequired={true}
                            name={ORGANIZATION_NAME_FIELD_NAME}
                            placeholder={ORGANIZATION_NAME_PLACEHOLDER}
                        />
                    </OnboardingFormContainer>
                </VerticalContainer>
            </Wrapper>

            <OnboardingFooterText className={styles.footerText}>
                {ORGANIZATION_NAME_FOOTER_TEXT}
            </OnboardingFooterText>
        </Form>
    );
};

export default OnboardingOrganizationCreationPage;
